import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse, TransactionType } from '@shared/models';
import * as Response from '@shared/models/analysis/analysis.response.type';
import * as Request from '@shared/models/analysis/analysis.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { fetchAnalysisProjects } from './project.queries';
import { fetchAnalysisReportList } from './report.queries';
import { formatStringIntoDate } from '@shared/helpers';
import {
    fetchTransactionsHistory,
    fetchSkillsProgress,
    fetchTrackProgress,
    fetchAssesmentResults,
} from '../../dashboard/queries';
import { fetchCertificateList } from '../../showcase/queries';
import { setWeeklyPointsHistory, setWeeklyTimeSpent } from '../slices/dashboard.slice';

export const AnalysisDashboardEndpoints = {
    getDashboardData: config.base_url + '/analysis/dashboard',
    getWeeklyPoints: config.base_url + '/analysis/weekly-points',
    getWeeklyTimeSpent: config.base_url + '/time-spent/weekly',
};

export const fetchWeeklyPointsHistory = createAsyncThunk<void, Request.IFetchWeeklyPointsHistory>(
    AnalysisDashboardEndpoints.getWeeklyPoints,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IWeeklyPointsHistoryResponse>>(
                AnalysisDashboardEndpoints.getWeeklyPoints
            );

            dispatch(setWeeklyPointsHistory(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching weekly points history, query: ${AnalysisDashboardEndpoints.getWeeklyPoints}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchWeeklyTimeSpent = createAsyncThunk<void, Request.IFetchWeeklyTimeSpent>(
    AnalysisDashboardEndpoints.getWeeklyTimeSpent,
    async (query, { dispatch }) => {
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res = await httpService.get<AscResponse<Response.IWeeklyTimeSpentResponse>>(
                `${AnalysisDashboardEndpoints.getWeeklyTimeSpent}?timezone=${timezone}`
            );

            dispatch(setWeeklyTimeSpent(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching weekly time spent, query: ${AnalysisDashboardEndpoints.getWeeklyTimeSpent}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchAnalysisDashboardData = createAsyncThunk<void>(
    AnalysisDashboardEndpoints.getDashboardData,
    async (query, { dispatch }) => {
        try {
            const date = new Date();
            dispatch(fetchCertificateList({ params: { take: '1', skip: '0', sortBy: 'earned' } }));
            dispatch(fetchWeeklyPointsHistory({}));
            dispatch(fetchWeeklyTimeSpent({}));
            dispatch(fetchSkillsProgress({}));
            dispatch(fetchTrackProgress({}));
            dispatch(fetchAnalysisProjects({}));
            dispatch(fetchAssesmentResults({}));
            dispatch(
                fetchAnalysisReportList({
                    params: {
                        from: formatStringIntoDate('01/01/2024'),
                        to: formatStringIntoDate(dayjs(date.setDate(date.getDate() + 1)).format('DD/MM/YYYY')),
                    },
                })
            );
            dispatch(
                fetchTransactionsHistory({
                    params: {
                        from: '',
                        to: '',
                        type: TransactionType.ALL,
                    },
                })
            );
        } catch (err) {
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching dashboard data of analysis, query: ${AnalysisDashboardEndpoints.getDashboardData}`
                ),
                { type: 'error' }
            );
        }
    }
);
